import { pathOr } from "ramda";
import {
    request,
    HttpParameter,
    HttpMethod,
    HttpParameterSubRoute,
} from "src/Commons/Http";
import { APPLICATION_NAME, DEFAULT_RESTAURANT_BRAND_COLOR } from "src/constant";
import { HttpRequestHeader } from "src/Shared/Enums/HttpRequestHeader";
import { LocalStorageKeys } from "src/Shared/Enums/LocalStorageKeys";
import { applyPrimaryColor } from "src/Shared/Utils/HTMLUtils";
import { getSessionToken, getToken } from "src/Shared/Utils/LocalStorageUtils";
import {
    AddDeviceTokenPayload,
    AccountDetailsResult,
    AccountReferenceResult,
    AuthenticationResult,
    GetOtpReferenceResult,
    RestuarantDetailsResult,
    SessionResult,
    VerifyMobileNumberResult,
    VerifyOtpResult,
    DeviceTokenResult,
    ListenerTokenResult,
} from "../Models";
import { DeleteDeviceTokenPayload } from "../Models/DeleteDeviceTokenPayload";

const SESSION_API_PATH = "Sessions/";
const MOBILE_NUMBER_API_PATH = "MobileNumbers/";
export const ACCOUNTS_API_PATH = "Accounts/";
export const RESTAURANTS_API_PATH = "Restaurants";

export const getUnAuthenticatedHeader = () => [
    {
        key: HttpRequestHeader.Authorization,
        value: `Bearer ${getSessionToken()}`,
    },
    {
        key: HttpRequestHeader.ApplicatioName,
        value: APPLICATION_NAME,
    },
];

export const getAuthenticatedHeader = () => [
    {
        key: HttpRequestHeader.Authorization,
        value: `Bearer ${getToken()}`,
    },
    {
        key: HttpRequestHeader.ApplicatioName,
        value: APPLICATION_NAME,
    },
];

export const getAuthenticatedHeaderAsObject = () => {
    return getAuthenticatedHeader().reduce(
        (obj, item) => Object.assign(obj, { [item.key]: item.value }),
        {}
    );
};

export const isAuthUser = () => {
    let Login = false;
    if (
        localStorage.getItem(LocalStorageKeys.AuthToken) &&
        !localStorage.getItem(LocalStorageKeys.IsRegisterRestuarant)
    ) {
        Login = true;
    }
    return Login;
};

export const getCurrentSessionToken = async () => {
    const configRequest = {
        endPoint: SESSION_API_PATH,
        method: HttpMethod.GET,
        subRoute: HttpParameterSubRoute.RestaurantAccount,
        headers: getUnAuthenticatedHeader(),
    } as HttpParameter;

    const result = await request(configRequest);
    let token = "";
    let errorMessage = "";
    if (result.success) {
        if (result?.headers?.token) token = result?.headers?.token;
    } else {
        errorMessage = result.errorMessage;
    }
    return { token, errorMessage } as SessionResult;
};

export const verifyMobileNumber = async (mobileNumber: any) => {
    const configRequest = {
        endPoint: MOBILE_NUMBER_API_PATH,
        method: HttpMethod.POST,
        subRoute: HttpParameterSubRoute.RestaurantAccount,
        headers: getUnAuthenticatedHeader(),
        body: mobileNumber,
    };

    const result = await request(configRequest);
    let refId = "";
    let errorMessage = "";

    if (result.success) {
        refId = pathOr("", ["response", "refId"], result);
    } else {
        // handle Errors
        errorMessage = result.errorMessage;
    }
    return { refId, errorMessage } as VerifyMobileNumberResult;
};

export const verifyMobileNumberForPWALogin = async (mobileNumber: any) => {
    const configRequest = {
        endPoint: `${MOBILE_NUMBER_API_PATH}Registered`,
        method: HttpMethod.POST,
        subRoute: HttpParameterSubRoute.RestaurantAccount,
        headers: getUnAuthenticatedHeader(),
        body: mobileNumber,
    };

    const result = await request(configRequest);

    let refId = "";
    let errorMessage = "";

    if (result.success) {
        refId = pathOr("", ["response", "refId"], result);
    } else {
        // handle Errors
        errorMessage = result.errorResponse.message;
    }
    return { refId, errorMessage } as VerifyMobileNumberResult;
};

export const getOtpReference = async (mobileNumberRefId: string) => {
    const configRequest = {
        endPoint: `${MOBILE_NUMBER_API_PATH}${mobileNumberRefId}/OTP`,
        method: HttpMethod.GET,
        subRoute: HttpParameterSubRoute.RestaurantAccount,
        headers: getUnAuthenticatedHeader(),
    };
    const result = await request(configRequest);
    let refId = "";
    let errorMessage = "";

    if (result.success) {
        refId = pathOr("", ["response", "refId"], result);
    } else {
        // handle Errors
        errorMessage = result.errorMessage;
    }
    return { refId, errorMessage } as GetOtpReferenceResult;
};

export const verifyOTP = async (
    mobileNumberId: string,
    otpId: string,
    otp: string
) => {
    const configRequest = {
        endPoint: `${MOBILE_NUMBER_API_PATH}${mobileNumberId}/OTP/${otpId}/VerificationStatus`,
        method: HttpMethod.PUT,
        subRoute: HttpParameterSubRoute.RestaurantAccount,
        headers: getUnAuthenticatedHeader(),
        body: {
            otp,
        },
    };
    const result = await request(configRequest);
    let refId = "";
    let errorMessage = "";

    if (result.success) {
        refId = pathOr("", ["response", "refId"], result);
    } else {
        // handle Errors
        errorMessage = result.errorMessage;
    }
    return { refId, errorMessage } as VerifyOtpResult;
};

export const getAccountIdFromMobileNumber = async (
    mobileNumberRefId: string
) => {
    const configRequest = {
        endPoint: `${MOBILE_NUMBER_API_PATH}${mobileNumberRefId}/Account`,
        method: HttpMethod.GET,
        subRoute: HttpParameterSubRoute.RestaurantAccount,
        headers: getUnAuthenticatedHeader(),
    };
    const result = await request(configRequest);
    let refId = "";
    let errorMessage = "";
    let isEmployee = "";
    if (result.success) {
        refId = pathOr("", ["response", "refId"], result);
        isEmployee = pathOr("", ["response", "isEmployee"], result);
    } else {
        // handle Errors
        errorMessage = result.errorMessage;
    }
    return { refId, isEmployee, errorMessage } as AccountReferenceResult;
};

export const getAuthenticationToken = async (
    accountId: string,
    isEmployee?: string
) => {
    const configRequest = {
        endPoint: `${ACCOUNTS_API_PATH}${accountId}/Authenticate${
            isEmployee === undefined ? "" : `?isEmployee=${isEmployee}`
        }`,
        method: HttpMethod.GET,
        subRoute: HttpParameterSubRoute.RestaurantAccount,
        headers: getUnAuthenticatedHeader(),
    };
    const result = await request(configRequest);
    let token = "";
    let errorMessage = "";
    if (result.success) {
        if (result?.headers?.token) token = result?.headers?.token;
    } else {
        errorMessage = result.errorMessage;
    }
    return { token, errorMessage } as AuthenticationResult;
};

export const getAccountDetailsByAccountId = async (accountId: string) => {
    const configRequest = {
        endPoint: `${ACCOUNTS_API_PATH}${accountId}`,
        method: HttpMethod.GET,
        subRoute: HttpParameterSubRoute.RestaurantAccount,
        headers: getAuthenticatedHeader(),
    };
    const result = await request(configRequest);
    let response = {};
    let errorMessage = "";
    if (result.success) {
        response = result.response;
    } else {
        errorMessage = result.errorMessage;
    }
    return {
        response,
        errorMessage,
        errorResponse: result.errorResponse,
    } as AccountDetailsResult;
};

export const getRestaurantDetailsUsingAccountId = async (accountId: string) => {
    const configRequest = {
        endPoint: `${RESTAURANTS_API_PATH}?accountRefId=${accountId}`,
        method: HttpMethod.GET,
        subRoute: HttpParameterSubRoute.Restaurant,
        headers: getAuthenticatedHeader(),
    };
    const result = await request(configRequest);
    let response = {};
    let errorMessage = "";

    if (result.success) {
        response = result.response;
    } else {
        errorMessage = result.errorMessage;
    }
    return { response, errorMessage } as RestuarantDetailsResult;
};

export const addDeviceToken = async (
    token: string,
    restuarantRefId: string,
    accountId: string
) => {
    const configRequest = {
        endPoint: `Registrations/${RESTAURANTS_API_PATH}/${restuarantRefId}/Users/${accountId}`,
        method: HttpMethod.POST,
        subRoute: HttpParameterSubRoute.Notification,
        headers: getAuthenticatedHeader(),
        body: {
            token,
            deviceId: restuarantRefId,
            deviceType: window.navigator.userAgent,
            platform: "Web",
        } as AddDeviceTokenPayload,
    };
    const result = await request(configRequest);
    let refId = "";
    let errorMessage = "";

    if (result.success) {
        refId = result.response;
    } else {
        errorMessage = result.errorMessage;
    }
    return { refId, errorMessage } as DeviceTokenResult;
};

export const deleteDeviceToken = async (
    accountId: string,
    restuarantRefId: string,
    payload: DeleteDeviceTokenPayload
) => {
    const configRequest = {
        endPoint: `Registrations/Restaurants/${restuarantRefId}/Users/${accountId}`,
        method: HttpMethod.DELETE,
        subRoute: HttpParameterSubRoute.Notification,
        headers: getAuthenticatedHeader(),
        body: payload,
    };
    const result = await request(configRequest);
    let refId = "";
    let errorMessage = "";

    if (result.success) {
        refId = result.response;
    } else {
        errorMessage = result.errorMessage;
    }
    return { refId, errorMessage } as DeviceTokenResult;
};

export const logout = (restuarantRefId: string): void => {
    const accountRefId = localStorage.getItem(LocalStorageKeys.AccountRefId);
    const deviceToken = localStorage.getItem(LocalStorageKeys.DeviceToken);
    if (accountRefId && deviceToken) {

        deleteDeviceToken(accountRefId, restuarantRefId, {
            token: deviceToken,
        });
    }
    if (window.navigator && navigator.serviceWorker) {
        navigator.serviceWorker
            .getRegistrations()
            .then(function (registrations) {
                for (let registration of registrations) {
                    registration.unregister();
                }
            });
    }

    applyPrimaryColor(DEFAULT_RESTAURANT_BRAND_COLOR);
    localStorage.clear();
};

export const getListenerToken = async (restuarantRefId: string) => {
    const listenerTokenRequest = {
        endPoint: `Restaurants/${restuarantRefId}/ListenerToken`,
        method: HttpMethod.GET,
        subRoute: HttpParameterSubRoute.Restaurant,
        headers: getAuthenticatedHeader(),
    };
    const result = await request(listenerTokenRequest);
    let token = "";
    let errorMessage = "";

    if (result.success) {
        token = result.response;
    } else {
        errorMessage = result.errorMessage;
    }
    return { ...result.response, errorMessage } as ListenerTokenResult;
};
